import { useEffect, useMemo, useState } from "react";
import { pct, share, type BacktestCardRef } from "./cards";
import { useDataClient } from "./client";
import { CloseIcon } from "./icons";

/** The chat card for one run: opens the pane. */
export function BacktestCard({ card, onOpen }: { card: BacktestCardRef; onOpen: (runId: string) => void }) {
  const title = card.strategyName || card.name || "Backtest";
  const sub = [
    card.version != null ? `v${card.version}` : null,
    card.finalCheck ? "final check" : null,
    card.period[0] && card.period[1] ? `${card.period[0]} → ${card.period[1]}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const failed = card.status !== "done";
  return (
    <button className="bt-card" onClick={() => onOpen(card.runId)} title="Open the backtest">
      <span className="bt-card-kind">Backtest</span>
      <span className="bt-card-main">
        <b>{title}</b>
        <span className="bt-card-sub">{sub}</span>
      </span>
      {failed ? (
        <span className="bt-card-stat bad">failed</span>
      ) : (
        <span className="bt-card-stat">
          {pct(card.totalReturn, 0)}
          <span className="dim"> vs {card.benchmark?.symbol ?? "bench"} {pct(card.benchmark?.total_return, 0)}</span>
          <span className="dim"> · DD {pct(card.maxDrawdown, 0)}</span>
        </span>
      )}
    </button>
  );
}

type Detail = {
  runId: string;
  folder: string;
  card: BacktestCardRef & Record<string, unknown>;
  summary: any;
  equity: [string, number | null, number | null][];
  tradeColumns: string[];
  trades: string[][];
  tradeCount: number;
  runs: any[];
};

/** Right-side pane for one run: Results · Trades · Runs (docs/040 §9). */
export function BacktestPane({ runId, onClose, onOpen }: { runId: string; onClose: () => void; onOpen: (id: string) => void }) {
  const client = useDataClient();
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState<"results" | "trades" | "runs">("results");

  useEffect(() => {
    setD(null);
    setErr("");
    client.backtestRun(runId)
      .then(setD)
      .catch((e) => setErr(String(e)));
  }, [runId]);

  const m = d?.summary?.metrics ?? {};
  const b = d?.summary?.benchmark ?? {};
  const title = d?.card?.strategyName || d?.card?.name || "Backtest";

  return (
    <aside className="bt-pane" aria-label="Backtest">
      <div className="bt-bar">
        <div className="bt-title">
          <b>{title}</b>
          <span className="dim">
            {d?.card?.version != null ? ` v${d.card.version}` : ""}
            {d?.card?.finalCheck ? " · final check" : ""}
            {d?.summary?.period ? ` · ${d.summary.period[0]} → ${d.summary.period[1]}` : ""}
          </span>
        </div>
        <button className="icon-btn" onClick={onClose} aria-label="Close backtest">
          <CloseIcon />
        </button>
      </div>
      <div className="bt-tabs" role="tablist">
        {(["results", "trades", "runs"] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? "on" : ""} onClick={() => setTab(t)}>
            {t === "results" ? "Results" : t === "trades" ? `Trades${d ? ` (${d.tradeCount})` : ""}` : `Runs${d ? ` (${d.runs.length || 1})` : ""}`}
          </button>
        ))}
      </div>
      <div className="bt-body">
        {err && <div className="bt-error">{err}</div>}
        {!d && !err && <div className="dim">Loading…</div>}
        {d && d.summary?.status !== "done" && (
          <div className="bt-error">This run failed: {d.summary?.error ?? d.summary?.status}</div>
        )}
        {d && d.summary?.status === "done" && tab === "results" && (
          <>
            <table className="bt-metrics">
              <thead>
                <tr><th></th><th>Strategy</th><th>{b.symbol ?? "Benchmark"}</th></tr>
              </thead>
              <tbody>
                <tr><td>Total return</td><td>{pct(m.total_return)}</td><td>{pct(b.total_return)}</td></tr>
                <tr><td>CAGR</td><td>{pct(m.cagr)}</td><td>{pct(b.cagr)}</td></tr>
                <tr><td>Max drawdown</td><td>{pct(m.max_drawdown)}</td><td>{pct(b.max_drawdown)}</td></tr>
                <tr><td>Sharpe</td><td>{m.sharpe?.toFixed?.(2) ?? "—"}</td><td>{b.sharpe?.toFixed?.(2) ?? "—"}</td></tr>
                <tr><td>Trades · win rate</td><td>{m.trades ?? "—"} · {share(m.win_rate)} wins</td><td></td></tr>
                <tr><td>Time invested</td><td>{share(m.avg_exposure)}</td><td>100%</td></tr>
              </tbody>
            </table>
            <EquityChart points={d.equity} benchLabel={b.symbol} />
            <Yearly strat={d.summary.yearly_returns ?? {}} bench={b.yearly_returns ?? {}} />
            <div className="bt-foot dim">
              Costs {d.summary.costs?.commission_bps} bps commission + {d.summary.costs?.slippage_bps} bps slippage ·
              benchmark {b.basis ?? "price"} · not investment advice
              {client.capabilities.localPaths && (
                <>
                  <br />
                  <span className="bt-path" title={d.folder}>{d.folder}</span>
                </>
              )}
            </div>
          </>
        )}
        {d && tab === "trades" && (
          <div className="bt-tablewrap">
            <table className="bt-table">
              <thead><tr>{d.tradeColumns.map((c) => <th key={c}>{c.replace(/_/g, " ")}</th>)}</tr></thead>
              <tbody>
                {[...d.trades].reverse().map((r, i) => (
                  <tr key={i}>
                    {r.map((v, j) => {
                      const col = d.tradeColumns[j];
                      const n = Number(v);
                      const isPct = col === "pnl_pct";
                      return (
                        <td key={j} className={isPct ? (n > 0 ? "up" : "down") : ""}>
                          {isPct ? pct(n) : v}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            {d.tradeCount > d.trades.length && <div className="dim">Newest {d.trades.length} of {d.tradeCount}; all are in round_trips.csv.</div>}
          </div>
        )}
        {d && tab === "runs" && (
          <div className="bt-tablewrap">
            {d.runs.length === 0 ? (
              <div className="dim">This run isn't saved under a strategy, so it has no other versions.</div>
            ) : (
              <table className="bt-table">
                <thead><tr><th>Version</th><th>Name</th><th>Total</th><th>Max DD</th><th>Trades</th><th></th></tr></thead>
                <tbody>
                  {d.runs.map((r) => (
                    <tr key={r.run_id} className={r.run_id === runId ? "current" : "clickable"} onClick={() => r.run_id !== runId && onOpen(r.run_id)}>
                      <td>v{r.version ?? "?"}</td>
                      <td>{r.name || "—"}</td>
                      <td>{pct(r.total_return)}</td>
                      <td>{pct(r.max_drawdown)}</td>
                      <td>{r.trades ?? "—"}</td>
                      <td className="dim">{r.final_check ? "final check" : r.status !== "done" ? r.status : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}

function EquityChart({ points, benchLabel }: { points: [string, number | null, number | null][]; benchLabel?: string }) {
  const W = 560, H = 200, P = 6;
  const { s, bch, lo, hi } = useMemo(() => {
    const vals = points.flatMap((p) => [p[1], p[2]]).filter((v): v is number => v != null && v > 0);
    const lo = Math.min(...vals), hi = Math.max(...vals);
    const x = (i: number) => P + (i / Math.max(points.length - 1, 1)) * (W - 2 * P);
    // Log scale: a 20-year curve on a linear axis hides every early drawdown.
    const y = (v: number) => H - P - ((Math.log(v) - Math.log(lo)) / (Math.log(hi) - Math.log(lo) || 1)) * (H - 2 * P);
    const line = (k: 1 | 2) =>
      points.map((p, i) => (p[k] != null && p[k]! > 0 ? `${x(i).toFixed(1)},${y(p[k]!).toFixed(1)}` : null)).filter(Boolean).join(" ");
    return { s: line(1), bch: line(2), lo, hi };
  }, [points]);
  if (!points.length) return null;
  return (
    <figure className="bt-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Equity curve, strategy vs benchmark, log scale">
        {bch && <polyline points={bch} fill="none" stroke="var(--ab-dim, #8b94a5)" strokeWidth="1.2" strokeDasharray="4 3" />}
        <polyline points={s} fill="none" stroke="var(--ab-accent, #4f9cf9)" strokeWidth="1.8" />
      </svg>
      <figcaption className="dim">
        <span className="bt-key strat" /> Strategy <span className="bt-key bench" /> {benchLabel ?? "Benchmark"} · log scale ·{" "}
        {Math.round(lo).toLocaleString()} → {Math.round(hi).toLocaleString()} · {points[0][0]} → {points[points.length - 1][0]}
      </figcaption>
    </figure>
  );
}

function Yearly({ strat, bench }: { strat: Record<string, number>; bench: Record<string, number> }) {
  const years = Object.keys(strat).sort();
  if (!years.length) return null;
  return (
    <div className="bt-tablewrap">
      <table className="bt-table bt-yearly">
        <thead><tr><th>Year</th><th>Strategy</th><th>Benchmark</th><th>Difference</th></tr></thead>
        <tbody>
          {years.map((y) => {
            const a = strat[y], c = bench[y];
            const diff = a != null && c != null ? a - c : null;
            return (
              <tr key={y}>
                <td>{y}</td>
                <td className={a > 0 ? "up" : "down"}>{pct(a)}</td>
                <td>{pct(c)}</td>
                <td className={diff == null ? "" : diff >= 0 ? "up" : "down"}>{pct(diff)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
